import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import { PlatformApi } from '@/lib/api/platform/platformSdk';

/** Fresh as long as the scoped permissions are, so the two never disagree about the same page. */
const FRESH_MS = 5 * 60 * 1000;

const accessSchema = z.object({ accessLevel: z.enum(['read', 'write']).nullable() });

export type WorkspaceAccessLevel = z.infer<typeof accessSchema>['accessLevel'];

/**
 * The caller's read/write level on one workspace, from the same endpoint the services ask
 * (`GET /auth/permissions`, see `getResourcePermission`). The SDK's client defaults to the v1
 * answer, `{ accessLevel }`; `getScopedPermissions` overrides that to get the v2 permission list.
 */
export const workspaceAccessLevelQuery = (workspaceId: string) =>
  queryOptions({
    queryKey: ['user', 'workspace-access-level', workspaceId],
    queryFn: async (): Promise<WorkspaceAccessLevel> => {
      const { data } = await PlatformApi.platformApi.get<unknown>('/auth/permissions', {
        params: { type: 'workspace', identifier: workspaceId },
      });
      return accessSchema.parse(data).accessLevel;
    },
    enabled: Boolean(workspaceId),
    staleTime: FRESH_MS,
  });
